/* Analysis — what ORBIS Range measured, in the browser.
 *
 * The product's central claim is that it answers a question with a confidence interval. Until this
 * panel existed the answer could only be read in a terminal on the host that ran the batch, which
 * meant the one capability nothing else in the Deck can demonstrate was also the one a viewer
 * never saw.
 *
 * Three things here are not decoration:
 *
 *   * **The determinism banner comes first.** If the engine did not reproduce, every interval
 *     below it is measuring noise the analysis cannot account for. That is not a footnote.
 *   * **The method is on every row.** A rate and a mean are different claims, and a table that
 *     showed only the numbers would invite averaging them together in the reader's head.
 *   * **A measure that carries no information says so, in place of its interval.** In the demo
 *     scenario `time_to_first_shot_s` is 1.000 in every single run, because RED starts inside its
 *     own missile envelope. Rendered as a point estimate with a zero-width interval it reads like
 *     the most confident row on the page. It is a constant. Refusing to plot it is the difference
 *     between an analysis view and a chart.
 *
 * The panel is EXCON- and analyst-only, enforced by the Gateway. A refusal is shown as the
 * server's own sentence rather than an empty table — "no batches" and "you may not read batches"
 * are different statements and only one of them is true.
 */
import { useEffect, useState } from 'react'
import { ChevronLeft, RefreshCw, X } from 'lucide-react'
import { useDeck } from '../state/store'
import {
  fetchBatch,
  fetchBatches,
  formatInterval,
  formatValue,
  methodLabel,
  type BatchDocument,
  type BatchSummary,
  type Grade,
  type MeasureRow,
} from '../lib/experiments'

const GRADE_COLOUR: Record<Grade, string> = {
  ok: 'var(--color-text-1)',
  weak: 'var(--color-warn)',
  none: 'var(--color-text-3)',
}

const GRADE_LABEL: Record<Grade, string> = {
  ok: '',
  weak: 'WIDE',
  none: 'NO INFORMATION',
}

function shortTime(iso: string): string {
  if (!iso) return 'undated'
  return iso.replace('T', ' ').replace('Z', 'Z')
}

function DeterminismBanner({ status, detail }: { status: string; detail: string }) {
  if (status === 'FAILED') {
    return (
      <div
        className="px-3 py-2"
        style={{ background: 'var(--color-crit-dim, rgba(220,60,60,0.12))', fontSize: 10 }}
      >
        <div className="label" style={{ color: 'var(--color-crit)', fontSize: 9 }}>
          ✗ DETERMINISM FAILED — THE INTERVALS BELOW ARE UNSOUND
        </div>
        <div style={{ color: 'var(--color-text-2)' }}>{detail}</div>
      </div>
    )
  }
  if (status === 'verified') {
    return (
      <div className="px-3 py-1.5" style={{ fontSize: 9.5, color: 'var(--color-text-3)' }}>
        Determinism verified — the first seed was re-run and produced the same event digest.
      </div>
    )
  }
  return (
    <div className="px-3 py-1.5" style={{ fontSize: 9.5, color: 'var(--color-warn)' }}>
      Determinism NOT verified — this batch was run with the re-run check disabled, so nothing
      here rules out the engine having produced different results from the same seed.
    </div>
  )
}

function Row({ row }: { row: MeasureRow }) {
  const colour = GRADE_COLOUR[row.grade]
  return (
    <tr style={{ borderBottom: '1px solid var(--color-line-1)' }}>
      <td className="px-2 py-1.5" style={{ verticalAlign: 'top' }}>
        <div className="num" style={{ fontSize: 10.5, color: 'var(--color-text-1)' }}>
          {row.name}
        </div>
        {row.description && (
          <div style={{ fontSize: 9, color: 'var(--color-text-3)' }}>{row.description}</div>
        )}
        {row.note && (
          <div style={{ fontSize: 9, color: colour }}>{row.note}</div>
        )}
      </td>
      <td className="num px-2 py-1.5 text-right" style={{ fontSize: 11, color: colour }}>
        {row.grade === 'none' && row.observed === 0 ? '—' : formatValue(row.kind, row.estimate)}
      </td>
      <td
        className="num px-2 py-1.5 text-right"
        style={{ fontSize: 10, color: colour, whiteSpace: 'nowrap' }}
      >
        {formatInterval(row)}
      </td>
      <td className="num px-2 py-1.5 text-right" style={{ fontSize: 10, color: 'var(--color-text-3)' }}>
        {row.observed}
        {row.missing > 0 && (
          <span title={`${row.missing} run(s) where this measure did not apply`}> +{row.missing}✕</span>
        )}
      </td>
      <td className="px-2 py-1.5" style={{ fontSize: 9, color: 'var(--color-text-3)' }}>
        {methodLabel(row)}
        {GRADE_LABEL[row.grade] && (
          <div className="label" style={{ fontSize: 8, color: colour }}>
            {GRADE_LABEL[row.grade]}
          </div>
        )}
      </td>
    </tr>
  )
}

function BatchDetail({ batch, onBack }: { batch: BatchDocument; onBack: () => void }) {
  return (
    <>
      <DeterminismBanner status={batch.determinism} detail={batch.determinismDetail} />

      <div className="px-3 pb-1" style={{ fontSize: 9.5, color: 'var(--color-text-3)' }}>
        {batch.runs} runs × {batch.ticks}s simulated · {batch.wallSeconds.toFixed(1)}s wall ·{' '}
        {shortTime(batch.createdIso)}
        {batch.failed > 0 && (
          <span style={{ color: 'var(--color-warn)' }}> · {batch.failed} run(s) errored</span>
        )}
      </div>
      <div className="num px-3 pb-2" style={{ fontSize: 9, color: 'var(--color-text-3)' }}>
        {batch.scenario || '—'} · scenario {batch.scenarioDigest.slice(0, 12) || '—'} · dataset{' '}
        {batch.datasetDigest.slice(0, 12) || '—'} · engine {batch.engineVersion || '—'}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <table className="w-full" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-line-2)' }}>
              <th className="label px-2 py-1 text-left" style={{ fontSize: 8 }}>Measure</th>
              <th className="label px-2 py-1 text-right" style={{ fontSize: 8 }}>Estimate</th>
              <th className="label px-2 py-1 text-right" style={{ fontSize: 8 }}>95% interval</th>
              <th className="label px-2 py-1 text-right" style={{ fontSize: 8 }}>n</th>
              <th className="label px-2 py-1 text-left" style={{ fontSize: 8 }}>Method</th>
            </tr>
          </thead>
          <tbody>
            {batch.rows.map((row) => (
              <Row key={row.name} row={row} />
            ))}
          </tbody>
        </table>
      </div>

      <div
        className="shrink-0 px-3 py-1.5"
        style={{
          fontSize: 9,
          color: 'var(--color-text-3)',
          borderTop: '1px solid var(--color-line-2)',
        }}
      >
        <button
          className="ctl mb-1"
          style={{ height: 20, padding: '0 6px', fontSize: 10 }}
          onClick={onBack}
        >
          <ChevronLeft size={11} /> All batches
        </button>
        <div>
          A rate carries a Wilson interval and a mean a Student-t one: they are different claims and
          are not comparable as numbers. `n` is the runs where the measure applied; ✕ counts the
          runs where it did not, which are excluded rather than averaged in as zero.
        </div>
      </div>
    </>
  )
}

export function AnalysisPanel() {
  const open = useDeck((s) => s.analysisOpen)
  const toggle = useDeck((s) => s.toggleAnalysis)
  const [items, setItems] = useState<BatchSummary[]>([])
  const [selected, setSelected] = useState<BatchDocument | null>(null)
  const [error, setError] = useState('')

  const load = async (openNewest = false) => {
    try {
      const batches = await fetchBatches()
      setItems(batches)
      setError('')
      // Open the newest batch straight away the first time the panel is opened. A panel that
      // lands on a list is a dead end when there is one obvious thing to read, and in a demo it
      // is an extra click between the question and the answer. The list stays one click back.
      if (openNewest && batches.length > 0 && !selected) {
        setSelected(await fetchBatch(batches[0].id))
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  useEffect(() => {
    if (!open) return
    void load(true)
    // Loaded once on open, not polled. A batch is a finished artefact — nothing about it changes
    // while it is on screen, and a poll would be a request per five seconds that can only ever
    // return the same bytes.
  }, [open])

  if (!open) return null

  const openBatch = async (id: string) => {
    try {
      setSelected(await fetchBatch(id))
      setError('')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <div
      className="panel absolute left-3 top-3 z-20 flex w-[34rem] flex-col overflow-hidden"
      style={{ maxHeight: 'calc(100% - 24px)' }}
    >
      <div
        className="flex shrink-0 items-center gap-2 px-3 py-2"
        style={{ borderBottom: '1px solid var(--color-line-2)' }}
      >
        <span className="title">{selected ? selected.experiment : 'Analysis — Range batches'}</span>
        {!selected && (
          <button
            className="ctl ml-auto"
            style={{ height: 20, padding: '0 6px', fontSize: 10 }}
            onClick={() => void load()}
            aria-label="Refresh the batch list"
            title="Refresh"
          >
            <RefreshCw size={11} />
          </button>
        )}
        <button
          className={selected ? 'ctl ml-auto' : 'ctl'}
          style={{ width: 20, height: 20, padding: 0 }}
          onClick={() => {
            setSelected(null)
            toggle()
          }}
          aria-label="Close the analysis panel"
        >
          <X size={12} />
        </button>
      </div>

      {error && (
        <p className="px-3 py-2" style={{ fontSize: 10.5, color: 'var(--color-crit)' }}>
          {error}
        </p>
      )}

      {selected ? (
        <BatchDetail batch={selected} onBack={() => setSelected(null)} />
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          {!error && items.length === 0 && (
            <p className="px-3 py-3" style={{ fontSize: 10.5, color: 'var(--color-text-3)' }}>
              No batches on this deployment. Run one with{' '}
              <code className="num">python -m orbis_range --runs 30 --store</code> and it appears
              here — the Deck reads results, it does not start them.
            </p>
          )}
          {items.map((b) => (
            <button
              key={b.id}
              className="block w-full px-3 py-2 text-left"
              style={{ borderBottom: '1px solid var(--color-line-1)', background: 'none' }}
              onClick={() => void openBatch(b.id)}
            >
              <div className="flex items-baseline gap-2">
                <span className="num" style={{ fontSize: 11.5, color: 'var(--color-text-1)' }}>
                  {b.experiment}
                </span>
                {b.determinism === 'FAILED' && (
                  <span className="label" style={{ fontSize: 8, color: 'var(--color-crit)' }}>
                    UNSOUND
                  </span>
                )}
                {b.failed > 0 && (
                  <span className="label" style={{ fontSize: 8, color: 'var(--color-warn)' }}>
                    {b.failed} ERRORED
                  </span>
                )}
                <span
                  className="num ml-auto"
                  style={{ fontSize: 9.5, color: 'var(--color-text-3)' }}
                >
                  {shortTime(b.createdIso)}
                </span>
              </div>
              <div className="num" style={{ fontSize: 9.5, color: 'var(--color-text-3)' }}>
                {b.runs} runs × {b.ticks}s · {b.measures} measures · scenario{' '}
                {b.scenarioDigest.slice(0, 12) || '—'}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
